import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Mail,
  Menu,
  Code,
  Briefcase,
  Sparkles,
  Rocket,
  Sun,
  Moon,
} from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { GradientText } from "../animated-text";
import { ConfettiButton } from "../confetti-button";
import { AnimatedIcon } from "../animated-icons";
import { theme } from "../../theme.js";
import Resume from "../../assets/files/Md Naimur Rahman.pdf";
import { useTheme } from "../theme-provider";

const NavigationBar = () => {
  const [activeSection, setActiveSection] = useState("home");
  const { theme: themeMode, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(themeMode === "light" ? "dark" : "light");
  };

  const downloadResume = () => {
    const link = document.createElement("a");
    link.href = Resume;
    link.target = "_blank";
    link.download = "Md Naimur Rahman.pdf";
    link.click();
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "skills", "experience", "projects", "contact"];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <header className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-5xl rounded-full bg-white/40 dark:bg-black/40 backdrop-blur-xl border border-black/10 dark:border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] transition-all duration-300">
      <div className="px-6 py-3 flex justify-between items-center">
        <Link to="/" className="text-xl font-semibold tracking-tight heading-font">
          <GradientText
            text="Md. Naimur Rahman"
            gradient={`bg-gradient-to-r ${theme.primary}`}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {[
            { name: "Home", id: "home", icon: Sparkles },
            { name: "Skills", id: "skills", icon: Code },
            { name: "Experience", id: "experience", icon: Briefcase },
            { name: "Projects", id: "projects", icon: Rocket },
            { name: "Contact", id: "contact", icon: Mail },
          ].map((item) => (
            <Link
              key={item.name}
              to={`#${item.id}`}
              className={`text-sm hover:text-black dark:hover:text-white transition-colors flex items-center gap-2 heading-font ${activeSection === item.id ? "text-black dark:text-white" : "text-zinc-500 dark:text-zinc-400"
                }`}
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById(item.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <AnimatedIcon
                icon={item.icon}
                size={16}
                color={
                  activeSection === item.id
                    ? theme.iconColors[0]
                    : "currentColor"
                }
              />
              {item.name}
            </Link>
          ))}
          <ConfettiButton
            variant="outline"
            className={`border-black/20 dark:border-white/20 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-black dark:text-white rounded-full heading-font`}
            confettiColors={theme.iconColors}
            onClick={downloadResume}
          >
            Resume <ArrowRight className="ml-2 h-4 w-4" />
          </ConfettiButton>
          <button onClick={toggleTheme} aria-label="Toggle Theme" className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-black dark:text-white">
            {themeMode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        <div className="flex md:hidden items-center gap-4">
          <button onClick={toggleTheme} aria-label="Toggle Theme" className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-black dark:text-white">
            {themeMode === "dark" ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open Menu" className="text-black dark:text-white">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white/95 dark:bg-black/95 backdrop-blur-2xl border-l-black/10 dark:border-l-white/10 w-[300px] flex flex-col justify-center">
              <div className="flex flex-col gap-8 w-full px-4">
                {[
                  { name: "Home", id: "home", icon: Sparkles },
                  { name: "Skills", id: "skills", icon: Code },
                  { name: "Experience", id: "experience", icon: Briefcase },
                  { name: "Projects", id: "projects", icon: Rocket },
                  { name: "Contact", id: "contact", icon: Mail },
                ].map((item) => (
                  <Link
                    key={item.name}
                    to={`#${item.id}`}
                    className={`text-2xl font-light hover:text-black dark:hover:text-white transition-colors flex items-center gap-4 heading-font ${activeSection === item.id ? "text-cyan-600 dark:text-cyan-400 font-medium" : "text-zinc-500"
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById(item.id)
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    <AnimatedIcon icon={item.icon} color={activeSection === item.id ? theme.iconColors[0] : "currentColor"} size={24} />
                    {item.name}
                  </Link>
                ))}
                <ConfettiButton
                  variant="outline"
                  className={`border-black/20 dark:border-white/20 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-black dark:text-white rounded-full heading-font mt-8 py-6 text-lg`}
                  confettiColors={theme.iconColors}
                  onClick={downloadResume}
                >
                  Download Resume <ArrowRight className="ml-2 h-5 w-5" />
                </ConfettiButton>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default NavigationBar;
